import { create } from 'zustand';
import { createClient } from '@supabase/supabase-js';
import logger from '@/logger';
import { OfferingVehicle } from '@/pages/CustomerSupport/types';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
const supabase = createClient(supabaseUrl, supabaseKey);

type CreateOfferingVehicle = Omit<
	OfferingVehicle,
	'id' | 'created_at' | 'updated_at' | 'organization_id'
>;
type UpdateOfferingVehicle = Partial<
	Omit<OfferingVehicle, 'id' | 'created_at' | 'updated_at' | 'organization_id'>
>;

interface OfferingVehiclesStore {
	vehicles: OfferingVehicle[];
	isLoading: boolean;
	error: string | null;

	loadVehicles: (organizationId: string) => Promise<void>;
	insertVehicle: (organizationId: string, vehicle: CreateOfferingVehicle) => Promise<boolean>;
	updateVehicle: (
		id: string,
		organizationId: string,
		updates: UpdateOfferingVehicle
	) => Promise<boolean>;
	deleteVehicle: (id: string, organizationId: string) => Promise<boolean>;
	resetError: () => void;
}

export const useOfferingVehiclesStore = create<OfferingVehiclesStore>((set, get) => ({
	vehicles: [],
	isLoading: false,
	error: null,

	loadVehicles: async (organizationId: string) => {
		try {
			set({ isLoading: true, error: null });

			const { data, error } = await supabase
				.from('finclu_customer_offering_vehicles')
				.select('*')
				.eq('organization_id', organizationId)
				.order('recommendation_priority', { ascending: true });

			if (error) {
				logger.error('Error loading offering vehicles:', error);
				set({ error: error.message, isLoading: false });
				return;
			}

			set({
				vehicles: data || [],
				isLoading: false
			});
		} catch (err) {
			logger.error('Error loading offering vehicles:', err);
			set({
				error: 'Failed to load offering vehicles',
				isLoading: false
			});
		}
	},

	insertVehicle: async (organizationId: string, vehicle: CreateOfferingVehicle) => {
		try {
			set({ error: null });

			const { error: insertError } = await supabase
				.from('finclu_customer_offering_vehicles')
				.insert([
					{
						organization_id: organizationId,
						name: vehicle.name,
						minimum_weekly_payment: vehicle.minimum_weekly_payment,
						fuel_type: vehicle.fuel_type,
						category: vehicle.category,
						description: vehicle.description,
						is_active: vehicle.is_active,
						recommendation_priority: vehicle.recommendation_priority ?? 0
					}
				]);

			if (insertError) {
				logger.error('Error inserting offering vehicle:', insertError);
				set({ error: insertError.message });
				return false;
			}

			await get().loadVehicles(organizationId);
			return true;
		} catch (err) {
			logger.error('Error inserting offering vehicle:', err);
			set({ error: 'Failed to insert offering vehicle' });
			return false;
		}
	},

	updateVehicle: async (id: string, organizationId: string, updates: UpdateOfferingVehicle) => {
		try {
			set({ error: null });

			const { error: updateError } = await supabase
				.from('finclu_customer_offering_vehicles')
				.update({
					...updates,
					updated_at: new Date().toISOString()
				})
				.eq('id', id);

			if (updateError) {
				logger.error('Error updating offering vehicle:', updateError);
				set({ error: updateError.message });
				return false;
			}

			await get().loadVehicles(organizationId);
			return true;
		} catch (err) {
			logger.error('Error updating offering vehicle:', err);
			set({ error: 'Failed to update offering vehicle' });
			return false;
		}
	},

	deleteVehicle: async (id: string, organizationId: string) => {
		try {
			set({ error: null });

			const { error: deleteError } = await supabase
				.from('finclu_customer_offering_vehicles')
				.delete()
				.eq('id', id);

			if (deleteError) {
				logger.error('Error deleting offering vehicle:', deleteError);
				set({ error: deleteError.message });
				return false;
			}

			await get().loadVehicles(organizationId);
			return true;
		} catch (err) {
			logger.error('Error deleting offering vehicle:', err);
			set({ error: 'Failed to delete offering vehicle' });
			return false;
		}
	},

	resetError: () => set({ error: null })
}));
